import { useState, useEffect, use } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import {
  ChevronRight,
  Search,
  Plus,
  ArrowLeft,
  ChevronDown,
  SlidersHorizontal,
  Layers,
  LayoutGrid,
  FolderOpen,
  CheckCircle2,
} from 'lucide-react';
import { useLocalStorage } from 'react-use';

// ── Service imports ──
import { fetchProjectsByCategory, fetchProjectCategoriesSummary, getUserByToken, fetchTbUserByNik } from '../../services/projectReportServices';

// ── Data imports ──
import { allCategories, iconMap } from '../../helpers/Project-Report/data/allCategories';
import { mergeCategoriesData } from '../../helpers/Project-Report/utils/mergeCategoriesData';

// ── Utils ──
import { listStatusConfig } from '../../helpers/Project-Report/utils/statusConfig';
import { getQueryParam } from '../../helpers/Project-Report/utils/params';

// ── Components ──
import ProjectCard from './components/ProjectCard';
import MiniStat from './components/MiniStat';
import CreateProjectModal from './components/CreateProjectModal';
import EditProjectModal from './components/EditProjectModal';

// Redux
import { useSelector } from 'react-redux';
import { useCurrentUserAccess } from '../../helpers/currentUserAccess';

// ─── Main Page ─────────────────────────────────────────────────────────────────
const ProjectList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { categoryId } = useParams();

  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeStatus, setActiveStatus] = useState('Semua');
  const [isLoading, setIsLoading] = useState(true);
  const [searchFocused, setSearchFocused] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [sortBy, setSortBy] = useState('default');
  const [catDropOpen, setCatDropOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const isEdited = getQueryParam(searchParams, 'isEdited');
  const onIframe = getQueryParam(searchParams, 'onIframe');
  const nik = searchParams.get('nik') || null;
  const [token, _] = useLocalStorage('token', '');
  const [user, setUser] = useState(null);
  const { name, permission } = useSelector((state) => state.user);
  const { isCheckingAuth } = useCurrentUserAccess(nik);


  // Data kategori dikirim via location.state saat navigate
  const fallback = allCategories[0];
  const stateCategory = location.state?.category;
  const catId = parseInt(categoryId, 10);
  const category = stateCategory || allCategories.find((c) => c.id === catId) || fallback;

  // Fetch API
  const loadProjects = async () => {
    setIsLoading(true);
    try {
      const mappedData = await fetchProjectsByCategory(catId);
      const result = await fetchProjectCategoriesSummary();
      const mergedCategories = mergeCategoriesData(result.categories, allCategories);

      setCategories(mergedCategories);
      setProjects(mappedData);
      setFiltered(mappedData);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
      setProjects([]);
      setFiltered([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUser = async () => {
    const response = await (nik ? fetchTbUserByNik(nik) : getUserByToken(token));
    if (response && (response.status === true || response.status === 'success' || response.status === 200)) {
      const userData = Array.isArray(response.data) ? response.data[0] : response.data;
      setUser(userData);
    }
  }

  useEffect(() => {
    fetchUser();
    loadProjects();
  }, [catId]);

  // Filter + search + sort
  useEffect(() => {
    let result = [...projects];
    if (activeStatus !== 'Semua') {
      result = result.filter((p) => p.status === activeStatus);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((p) => p.title.toLowerCase().includes(q) || p.description.toLowerCase().includes(q));
    }
    if (sortBy === 'progress_asc') result.sort((a, b) => a.progress - b.progress);
    if (sortBy === 'progress_desc') result.sort((a, b) => b.progress - a.progress);
    if (sortBy === 'title') result.sort((a, b) => a.title.localeCompare(b.title));
    setFiltered(result);
  }, [searchQuery, activeStatus, projects, sortBy]);

  const statusFilters = ['Semua', 'Berlangsung', 'Menunggu', 'Selesai'];
  const totalTasks = projects.reduce((s, p) => s + p.tasks.onProgress + p.tasks.waiting + p.tasks.onHold + p.tasks.done, 0);
  const totalDone = projects.reduce((s, p) => s + p.tasks.done, 0);
  const avgProgress = projects.length > 0 ? Math.round(projects.reduce((s, p) => s + p.progress, 0) / projects.length) : 0;

  const sortOptions = [
    { label: 'Default', value: 'default' },
    { label: 'Progress (Tinggi → Rendah)', value: 'progress_desc' },
    { label: 'Progress (Rendah → Tinggi)', value: 'progress_asc' },
    { label: 'Nama (A–Z)', value: 'title' },
  ];

  if (isCheckingAuth) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', width: '100vw', background: '#f8fafc' }}>
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2.5rem', color: '#f97316' }}></i>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #f8fafc 0%, #f1f5f9 100%)',
        padding: '32px 36px',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      }}
      onClick={() => { sortOpen && setSortOpen(false); catDropOpen && setCatDropOpen(false); }}
    >
      {/* ── Page header ── */}
      <div style={{ marginBottom: '24px' }}>
        {/* Breadcrumb */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', fontSize: '12px', color: '#94a3b8' }}>
          <span
            style={{ cursor: 'pointer', transition: 'color 0.2s' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#f97316')}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#94a3b8')}
            onClick={() => navigate({
              pathname: '/project-report',
              search: searchParams.toString(),
            })}
          >
            Project Categories
          </span>
          <ChevronRight size={12} />
          <span style={{ color: category.color, fontWeight: '600' }}>{category.name}</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            {/* Back button */}
            <button
              onClick={() => navigate({
                pathname: '/project-report',
                search: searchParams.toString(),
              })}
              style={{
                width: '38px',
                height: '38px',
                borderRadius: '10px',
                background: '#fff',
                border: '1.5px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
                flexShrink: 0,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = category.color; e.currentTarget.style.borderColor = category.color; e.currentTarget.querySelector('svg').style.color = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.querySelector('svg').style.color = '#64748b'; }}
            >
              <ArrowLeft size={16} style={{ color: '#64748b', transition: 'color 0.2s' }} />
            </button>

            <div>
              <h1
                style={{
                  fontSize: '28px',
                  fontWeight: '800',
                  color: '#0f172a',
                  margin: 0,
                  letterSpacing: '-0.5px',
                  lineHeight: 1.2,
                }}
              >
                Project Management
              </h1>
              <div
                style={{
                  fontSize: '14px',
                  color: '#64748b',
                  marginTop: '3px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <span
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: category.gradient,
                    display: 'inline-block',
                  }}
                />
                {category.name}
              </div>
            </div>
          </div>

          {/* Create project button */}
          {permission.includes("project_report:edit") && (
            <button
              onClick={() => setShowCreateModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '11px 22px',
                borderRadius: '12px',
                border: 'none',
                background: category.gradient,
                color: '#fff',
                fontSize: '13px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: `0 4px 16px ${category.color}50`,
                transition: 'all 0.2s',
                letterSpacing: '0.2px',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 8px 24px ${category.color}60`; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = `0 4px 16px ${category.color}50`; }}
            >
              <Plus size={16} strokeWidth={2.5} />
              Create Project
            </button>
          )}
        </div>
      </div>

      {/* ── Summary stats ── */}
      {!isLoading && (
        <div
          style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '22px',
            flexWrap: 'wrap',
          }}
        >
          <MiniStat value={projects.length} label="Total Proyek" color={category.color} />
          <MiniStat value={`${avgProgress}%`} label="Rata-rata Progress" color="#6366f1" />
          <MiniStat value={totalTasks} label="Total Task" color="#f59e0b" />
          <MiniStat value={totalDone} label="Task Selesai" color="#10b981" />
        </div>
      )}

      {/* ── Filter category dropdown ── */}
      <div style={{ position: 'relative', marginBottom: '14px' }} onClick={(e) => e.stopPropagation()}>
        {/* Label */}
        <div style={{ fontSize: '11px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <LayoutGrid size={11} />
          Filter Kategori
        </div>

        {/* Trigger button */}
        <button
          onClick={() => setCatDropOpen((p) => !p)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 16px',
            borderRadius: '14px',
            border: catDropOpen ? `2px solid ${category.color}` : '1.5px solid #e2e8f0',
            background: '#fff',
            cursor: 'pointer',
            boxShadow: catDropOpen ? `0 0 0 3px ${category.color}20` : '0 2px 8px rgba(0,0,0,0.05)',
            transition: 'all 0.2s',
            textAlign: 'left',
          }}
        >
          {/* Icon bubble */}
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: category.gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: `0 4px 12px ${category.color}40`,
            }}
          >
            {(() => { const IC = iconMap[category.icon] || FolderOpen; return <IC size={18} color="#fff" strokeWidth={1.8} />; })()}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {category.name}
            </div>
            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
              {category.totalProject ?? '–'} proyek · {category.activeProject ?? '–'} aktif
            </div>
          </div>

          {/* Tag pill */}
          <span style={{ fontSize: '11px', fontWeight: '600', padding: '3px 10px', borderRadius: '20px', background: `${category.color}15`, color: category.color, whiteSpace: 'nowrap', flexShrink: 0 }}>
            {category.tag}
          </span>

          <ChevronDown
            size={16}
            style={{ color: '#94a3b8', flexShrink: 0, transition: 'transform 0.25s', transform: catDropOpen ? 'rotate(180deg)' : 'none' }}
          />
        </button>

        {/* Dropdown panel */}
        {catDropOpen && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              left: 0,
              right: 0,
              background: '#fff',
              borderRadius: '16px',
              border: '1.5px solid #e2e8f0',
              boxShadow: '0 16px 48px rgba(0,0,0,0.14)',
              zIndex: 40,
              overflow: 'hidden',
              animation: 'dropFadeIn 0.2s ease',
            }}
          >
            {/* Panel header */}
            <div style={{ padding: '12px 16px 8px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <LayoutGrid size={13} color="#94a3b8" />
              <span style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Pilih Kategori Proyek</span>
            </div>

            {/* Category items */}
            <div style={{ maxHeight: '360px', overflowY: 'auto', padding: '8px' }}>
              {categories.map((cat) => {
                const IC = iconMap[cat.icon] || FolderOpen;
                const isSelected = cat.id === category.id;
                const pct = Math.round((cat.activeProject / cat.totalProject) * 100);
                return (
                  <div
                    key={cat.id}
                    onClick={() => {
                      setCatDropOpen(false);
                      const path = `/project-report/${cat.id}`;

                      navigate({
                        pathname: path,
                        search: searchParams.toString(),
                      }, {
                        state: { category: cat }
                      });
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 12px',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      background: isSelected ? `${cat.color}12` : 'transparent',
                      border: isSelected ? `1.5px solid ${cat.color}30` : '1.5px solid transparent',
                      transition: 'all 0.15s',
                      marginBottom: '4px',
                    }}
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                  >
                    {/* Icon */}
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '12px',
                        background: isSelected ? cat.gradient : cat.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        transition: 'background 0.2s',
                        boxShadow: isSelected ? `0 4px 10px ${cat.color}40` : 'none',
                      }}
                    >
                      <IC size={18} color="#fff" strokeWidth={1.8} />
                    </div>

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ fontSize: '13px', fontWeight: isSelected ? '700' : '600', color: isSelected ? cat.color : '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {cat.name}
                        </span>
                        <span style={{ fontSize: '10px', fontWeight: '700', color: '#fff', background: `${cat.color}cc`, padding: '2px 8px', borderRadius: '20px', flexShrink: 0 }}>
                          {cat.progress}%
                        </span>
                      </div>
                      {/* Mini progress bar */}
                      <div style={{ height: '4px', background: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${cat.progress}%`, background: cat.gradient, borderRadius: '99px' }} />
                      </div>
                      <div style={{ marginTop: '3px', fontSize: '10.5px', color: '#94a3b8' }}>
                        {cat.totalProject} project · <span style={{ color: '#22c55e' }}>{cat.activeProject} aktif</span>
                      </div>
                    </div>

                    {/* Selected indicator */}
                    {isSelected && (
                      <CheckCircle2 size={16} color={cat.color} style={{ flexShrink: 0 }} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Search + Sort bar ── */}
      <div
        style={{
          background: '#fff',
          border: '1.5px solid #f1f5f9',
          borderRadius: '14px',
          padding: '12px 16px',
          marginBottom: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <Search size={16} style={{ color: searchFocused ? category.color : '#94a3b8', transition: 'color 0.2s', flexShrink: 0 }} />
        <input
          type="text"
          placeholder="Cari proyek..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            fontSize: '13px',
            color: '#0f172a',
            background: 'transparent',
          }}
        />

        {/* Sort dropdown */}
        <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setSortOpen((p) => !p)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '7px 14px',
              borderRadius: '8px',
              border: '1.5px solid #e2e8f0',
              background: sortOpen ? `${category.color}10` : '#fafafa',
              color: sortOpen ? category.color : '#64748b',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap',
            }}
          >
            <SlidersHorizontal size={13} />
            Urutkan
            <ChevronDown size={12} style={{ transform: sortOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
          </button>
          {sortOpen && (
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: 'calc(100% + 6px)',
                background: '#fff',
                border: '1.5px solid #e2e8f0',
                borderRadius: '12px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                zIndex: 50,
                minWidth: '220px',
                overflow: 'hidden',
              }}
            >
              {sortOptions.map((opt) => (
                <div
                  key={opt.value}
                  onClick={() => { setSortBy(opt.value); setSortOpen(false); }}
                  style={{
                    padding: '10px 16px',
                    fontSize: '12.5px',
                    cursor: 'pointer',
                    fontWeight: sortBy === opt.value ? '700' : '400',
                    color: sortBy === opt.value ? category.color : '#374151',
                    background: sortBy === opt.value ? `${category.color}10` : 'transparent',
                    transition: 'background 0.15s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                  onMouseEnter={(e) => { if (sortBy !== opt.value) e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={(e) => { if (sortBy !== opt.value) e.currentTarget.style.background = 'transparent'; }}
                >
                  {sortBy === opt.value && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: category.color, flexShrink: 0 }} />}
                  {opt.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Status filter tabs ── */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {statusFilters.map((f) => {
          const isActive = activeStatus === f;
          const cfg = f !== 'Semua' ? Object.values(listStatusConfig).find((s) => s.label === f) : null;
          return (
            <button
              key={f}
              onClick={() => setActiveStatus(f)}
              style={{
                padding: '7px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '600',
                background: isActive ? category.gradient : '#fff',
                color: isActive ? '#fff' : '#64748b',
                boxShadow: isActive ? `0 4px 12px ${category.color}40` : '0 1px 4px rgba(0,0,0,0.06)',
                border: isActive ? 'none' : '1px solid #e2e8f0',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
              }}
            >
              {cfg && <cfg.icon size={11} />}
              {f}
            </button>
          );
        })}
      </div>

      {/* ── Result count ── */}
      <div style={{ marginBottom: '16px', fontSize: '13px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Layers size={14} />
        Menampilkan{' '}
        <span style={{ fontWeight: '700', color: '#0f172a' }}>{filtered.length}</span>{' '}
        dari{' '}
        <span style={{ fontWeight: '700', color: '#0f172a' }}>{projects.length}</span>{' '}
        proyek
      </div>

      {/* ── Project list ── */}
      {isLoading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              style={{
                height: '180px',
                borderRadius: '16px',
                background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 1.4s infinite',
              }}
            />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 20px', color: '#94a3b8' }}>
          <Search size={48} style={{ opacity: 0.3, marginBottom: '16px', display: 'block', margin: '0 auto 16px' }} />
          <div style={{ fontSize: '18px', fontWeight: '600', color: '#64748b' }}>Proyek tidak ditemukan</div>
          <div style={{ fontSize: '13px', marginTop: '6px' }}>Coba ubah kata kunci atau filter yang digunakan</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {filtered.map((project, idx) => (
            <ProjectCard
              key={project.id}
              project={project}
              categoryColor={category.color}
              categoryGradient={category.gradient}
              index={idx}
              category={category}
              categoryId={catId}
              onEdit={(!onIframe || isEdited) ? (p) => setEditingProjectId(p.id) : undefined}
              user={user}
            />
          ))}
        </div>
      )}

      {/* ── Keyframes ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        @keyframes shimmer {
          0%   { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes dropFadeIn {
          from { opacity: 0; transform: translateY(-8px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes modalIn {
          from { opacity: 0; transform: translate(-50%,-50%) scale(0.92); }
          to   { opacity: 1; transform: translate(-50%,-50%) scale(1); }
        }
        @keyframes backdropIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* ── Create Project Modal ── */}
      <CreateProjectModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        defaultCategory={category}
        accentColor={category.color}
        accentGradient={category.gradient}
        onSuccess={loadProjects}
        nik={nik}
      />
      <EditProjectModal
        isOpen={!!editingProjectId}
        onClose={() => setEditingProjectId(null)}
        projectId={editingProjectId}
        accentColor={category.color}
        accentGradient={category.gradient}
        onSuccess={loadProjects}
        nik={nik}
      />
    </div>
  );
};

export default ProjectList;
